import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 is expected when not signed in — only log unexpected failures.
      if (err?.response?.status && err.response.status !== 401) {
        console.error("Auth refresh failed:", err);
      }
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth callback, skip the /me check — the
    // AuthCallback page will exchange the session_id and hydrate the user.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Bearer fallback for browsers that block 3rd-party cookies (mobile Safari).
      // The primary auth is the httpOnly access_token cookie set by the backend.
      localStorage.setItem("riddhi_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      localStorage.setItem("riddhi_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    localStorage.removeItem("riddhi_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, checking, login, register, logout, refresh }),
    [user, checking, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
